// Read-only profile shown when you tap a class member or chat author. Mirrors
// iOS PublicProfileView (avatar, stats, shared sets, Message → open DM).
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getPublicProfile } from "@/api/users";
import { openDM } from "@/api/chat";
import { Avatar } from "@/components/avatar";
import { Button, Spinner } from "@/components/ui";

export function PublicProfileModal({ userId, onClose }: { userId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["public-profile", userId], queryFn: () => getPublicProfile(userId) });

  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);

  const p = q.data;
  const message = async () => {
    try {
      await openDM(userId);
      onClose();
      navigate("/app/community");
    } catch {
      /* the relationship gate may block — ignore here */
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {q.isLoading && (
          <div className="flex justify-center py-8 text-brand-600">
            <Spinner className="h-6 w-6" />
          </div>
        )}
        {q.isError && <p className="text-sm text-ink-faint">{t("Couldn't load profile.")}</p>}
        {p && (
          <>
            <div className="flex items-center gap-4">
              <Avatar url={p.avatar_url} name={p.display_name} size={64} />
              <div className="min-w-0">
                <h2 className="truncate text-lg font-bold">{p.display_name}</h2>
                {p.username && <p className="text-sm text-ink-faint">@{p.username}</p>}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <Stat label={t("Tier")} value={p.tier} />
              <Stat label={t("XP")} value={p.total_xp} />
              <Stat label={t("Streak")} value={p.current_streak} />
            </div>
            {p.public_question_sets.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
                  {t("Shared sets")}
                </p>
                <ul className="mt-1 space-y-1">
                  {p.public_question_sets.map((s) => (
                    <li key={s.id} className="truncate text-sm text-ink-soft">
                      {s.title}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={onClose}>
                {t("Close")}
              </Button>
              <Button size="sm" onClick={message}>
                {t("Message")}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="text-lg font-bold">{value}</div>
      <div className="text-xs text-ink-faint">{label}</div>
    </div>
  );
}
